/**
 * Custom hook for handling drag and drop file uploads
 *
 * Provides drag and drop functionality for file uploads with validation,
 * visual feedback, and integration with existing file upload system.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  validateImageFile,
  validateFile,
  SUPPORTED_MIME_TYPES,
  SUPPORTED_FILE_TYPES,
} from '../utils/file-upload';
import { logDebug, logError } from '../utils/safe-logger';

export type DragDropState = {
  isDragActive: boolean;
  isDragReject: boolean;
  draggedFiles: File[];
};

export type DragDropCallbacks = {
  onFilesDrop: (files: File[]) => void;
  onDragEnter?: (event: DragEvent) => void;
  onDragLeave?: (event: DragEvent) => void;
  onDragOver?: (event: DragEvent) => void;
  onError?: (error: string) => void;
};

export type UseDragAndDropOptions = {
  maxFiles?: number;
  disabled?: boolean;
  validateFiles?: boolean;
  acceptedFileTypes?: string[];
  /** Use mixed file validation (images + PDFs) instead of image-only validation */
  supportMixedFileTypes?: boolean;
};

export const useDragAndDrop = (
  callbacks: DragDropCallbacks,
  options: UseDragAndDropOptions = {}
) => {
  const {
    maxFiles = 5,
    disabled = false,
    validateFiles = true,
    acceptedFileTypes = options.supportMixedFileTypes ? SUPPORTED_FILE_TYPES : SUPPORTED_MIME_TYPES,
    supportMixedFileTypes = true, // Enable PDF support by default now
  } = options;

  const [dragState, setDragState] = useState<DragDropState>({
    isDragActive: false,
    isDragReject: false,
    draggedFiles: [],
  });

  const dragCounter = useRef(0);
  const dragTimeoutRef = useRef<NodeJS.Timeout>();

  const validateDroppedFiles = useCallback(
    (files: File[]): { validFiles: File[]; errors: string[] } => {
      const validFiles: File[] = [];
      const errors: string[] = [];

      if (files.length > maxFiles) {
        errors.push(`Cannot drop more than ${maxFiles} files at once`);
        return { validFiles: [], errors };
      }

      files.forEach((file, index) => {
        if (!validateFiles) {
          validFiles.push(file);
          return;
        }

        // Check file type
        if (!acceptedFileTypes.includes(file.type)) {
          errors.push(`"${file.name}" is not a supported file type`);
          return;
        }

        // Use appropriate validation function based on file type support
        const validation = supportMixedFileTypes ? validateFile(file) : validateImageFile(file);
        if (!validation.valid) {
          errors.push(`"${file.name}": ${validation.error}`);
          return;
        }

        validFiles.push(file);
      });

      return { validFiles, errors };
    },
    [maxFiles, validateFiles, acceptedFileTypes, supportMixedFileTypes]
  );

  const handleDragEnter = useCallback(
    (event: DragEvent) => {
      if (disabled) return;

      event.preventDefault();
      event.stopPropagation();

      dragCounter.current++;

      // Use a ref to check current state instead of including it in dependencies
      setDragState(prevState => {
        if (prevState.isDragActive) return prevState;

        if (event.dataTransfer?.items) {
          const items = Array.from(event.dataTransfer.items);
          const files = items
            .filter(item => item.kind === 'file')
            .map(item => item.getAsFile())
            .filter((file): file is File => file !== null);

          const hasInvalidFiles = files.some(
            file => validateFiles && !acceptedFileTypes.includes(file.type)
          );

          return {
            isDragActive: true,
            isDragReject: hasInvalidFiles || files.length > maxFiles,
            draggedFiles: files,
          };
        } else {
          return {
            isDragActive: true,
            isDragReject: false,
            draggedFiles: [],
          };
        }
      });

      callbacks.onDragEnter?.(event);
      logDebug('Drag enter detected', { fileCount: event.dataTransfer?.items.length || 0 });
    },
    [disabled, validateFiles, acceptedFileTypes, maxFiles, supportMixedFileTypes, callbacks]
  );

  const handleDragLeave = useCallback(
    (event: DragEvent) => {
      if (disabled) return;

      event.preventDefault();
      event.stopPropagation();

      dragCounter.current--;

      // Ensure counter doesn't go below 0
      if (dragCounter.current < 0) {
        dragCounter.current = 0;
      }

      // Clear timeout if it exists
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }

      // Only clear drag state when we've left all drag targets
      dragTimeoutRef.current = setTimeout(() => {
        if (dragCounter.current === 0) {
          setDragState(prevState => {
            if (!prevState.isDragActive) return prevState;
            return {
              isDragActive: false,
              isDragReject: false,
              draggedFiles: [],
            };
          });
          logDebug('Drag leave detected - cleared state');
        }
      }, 100); // Slightly longer delay to handle rapid enter/leave events

      callbacks.onDragLeave?.(event);
    },
    [disabled, callbacks]
  );

  const dragStateRef = useRef(dragState);
  dragStateRef.current = dragState;

  const handleDragOver = useCallback(
    (event: DragEvent) => {
      if (disabled) return;

      event.preventDefault();
      event.stopPropagation();

      // Set the dropEffect to indicate this is a valid drop zone
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = dragStateRef.current.isDragReject ? 'none' : 'copy';
      }

      callbacks.onDragOver?.(event);
    },
    [disabled, callbacks]
  );

  const handleDrop = useCallback(
    (event: DragEvent) => {
      if (disabled) return;

      event.preventDefault();
      event.stopPropagation();

      // Reset drag counter and state
      dragCounter.current = 0;
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }

      setDragState({
        isDragActive: false,
        isDragReject: false,
        draggedFiles: [],
      });

      const files = event.dataTransfer?.files;
      if (!files || files.length === 0) {
        logDebug('Drop event with no files');
        return;
      }

      const fileArray = Array.from(files);
      logDebug('Files dropped', {
        count: fileArray.length,
        fileNames: fileArray.map(f => f.name),
      });

      if (validateFiles) {
        const { validFiles, errors } = validateDroppedFiles(fileArray);

        if (errors.length > 0) {
          errors.forEach(error => {
            callbacks.onError?.(error);
            logError('Drag and drop validation error', error);
          });
        }

        if (validFiles.length > 0) {
          callbacks.onFilesDrop(validFiles);
          logDebug('Valid files processed', { count: validFiles.length });
        }
      } else {
        callbacks.onFilesDrop(fileArray);
      }
    },
    [disabled, validateFiles, validateDroppedFiles, callbacks]
  );

  // Get drag and drop event handlers for attaching to elements
  const getDragProps = useCallback(
    () => ({
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    }),
    [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]
  );

  // Reset drag state (useful for cleanup)
  const resetDragState = useCallback(() => {
    dragCounter.current = 0;
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
    }
    setDragState({
      isDragActive: false,
      isDragReject: false,
      draggedFiles: [],
    });
  }, []);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
    };
  }, []);

  // Reset state when disabled changes
  useEffect(() => {
    if (disabled) {
      resetDragState();
    }
  }, [disabled, resetDragState]);

  return {
    dragState,
    getDragProps,
    resetDragState,
  };
};
